import { adminApiAxios, apiAxios } from '@/api'
import type { DateTime } from 'luxon'
import { normalize } from 'normalizr'

import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import {
  Transaction,
  SplitTransactionModel,
  PostTransactionPayload,
  TransactionResponseEntities,
  receiveAdminAllTransactions,
  deleteAdminTransaction,
  deleteMultipleTransactions,
  receiveAdminTransactionsCounts,
} from '../reducers/admin/allTransactions.slice'
import { fetchWrapper } from '../reducers/fetch'
import { StripeSubscription } from '../reducers/subscription.slice'
import { transactionListSchema, transactionSchema } from '../schema'
import { receiveJournalEntries } from '../reducers/admin/journalEntryReducer'

export const FETCH_RECONCILIATION_TRANSACTIONS_KEY =
  'FETCH_RECONCILIATION_TRANSACTIONS_KEY'
export const fetchReconciliationTransactions = (
  userId: number,
  financialAccountId: number,
  startDate: DateTime,
  endDate: DateTime
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error fetching filtered transactions',
    fetchKey: FETCH_RECONCILIATION_TRANSACTIONS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<{
        count: number
        financialProfileId: number | null
        transactions: Transaction[]
      }>(
        `/finances/api/v1/admin/transactions/reconciliation/${userId}/${financialAccountId}/${startDate.toFormat(DATE_FORMATS_LUXON.API_INPUT)}/${endDate.toFormat(DATE_FORMATS_LUXON.API_INPUT)}`
      )
      const normalizedData = normalize<
        Transaction,
        TransactionResponseEntities,
        string[]
      >(json.data.transactions, transactionListSchema)
      dispatch(receiveAdminAllTransactions(normalizedData))
      return json.data
    },
  })

// Bulk Transactions Update

export type BulkUpdatePayload = PostTransactionPayload & {
  transactionIds: number[]
}
export const BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY =
  'BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY'
export const bulkUpdateTransactionsByAccount = (
  financialAccountId: number,
  data: Omit<BulkUpdatePayload, 'transactionIds'>
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error updating transactions by account',
    fetchKey: BULK_UPDATE_TRANSACTIONS_BY_ACCOUNT_KEY,
    fetchFunction: async () => {
      await adminApiAxios.post(
        `/finances/api/v1/admin/transactions/bulk/accounts/${financialAccountId}`,
        data
      )
      return true
    },
  })

export const BULK_UPDATE_USER_TRANSACTIONS_KEY =
  'BULK_UPDATE_USER_TRANSACTIONS_KEY'
export const bulkUpdateUserTransactions = (data: BulkUpdatePayload) =>
  fetchWrapper({
    defaultErrorMessage: 'Error updating all transactions',
    fetchKey: BULK_UPDATE_USER_TRANSACTIONS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<Transaction[]>(
        '/finances/api/v1/admin/transactions/bulk',
        data
      )
      const normalizedData = normalize<
        Transaction,
        TransactionResponseEntities,
        string[]
      >(json.data, transactionListSchema)
      dispatch(receiveAdminAllTransactions(normalizedData))
      if (normalizedData.entities.journalEntries) {
        dispatch(receiveJournalEntries(normalizedData.entities.journalEntries))
      }
      return json.data
    },
  })

// Fetch all Transaction Counts
export const fetchTransactionCounts = ({
  userId,
  startDate,
  endDate,
}: {
  userId: number | string | undefined
  startDate?: DateTime
  endDate?: DateTime
}) =>
  fetchWrapper({
    defaultErrorMessage: 'Error fetching transaction counts',
    defaultValue: {
      pendingCount: 0,
      uncategorizedCount: 0,
      requestedClarificationCount: 0,
      autoConfirmedCount: 0,
    },
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<{
        pendingCount: number
        uncategorizedCount: number
        requestedClarificationCount: number
        autoConfirmedCount: number
      }>('/finances/api/v1/admin/transactions/count', {
        params: {
          userId,
          startDate: startDate?.toFormat(DATE_FORMATS_LUXON.API_INPUT),
          endDate: endDate?.toFormat(DATE_FORMATS_LUXON.API_INPUT),
        },
      })
      dispatch(receiveAdminTransactionsCounts(json.data))
      return json.data
    },
  })

/*
  Create a transaction
  Currently via the Account Reconciliation panel
*/

export const adminCreateTransaction = (data: Partial<Transaction>) =>
  fetchWrapper({
    defaultErrorMessage: 'Error creating transaction',
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.post<Transaction>(
        '/finances/api/v1/transactions',
        data
      )
      const normalizedData = normalize<
        Transaction,
        TransactionResponseEntities,
        string[]
      >(json.data, transactionSchema)
      dispatch(receiveAdminAllTransactions(normalizedData))
      return json.data
    },
  })

// Delete a single transaction

export const adminDeleteTransaction = (id: number) =>
  fetchWrapper({
    defaultErrorMessage: 'Error deleting transaction',
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.delete<Transaction>(
        `/finances/api/v1/admin/transactions/delete/${id}`
      )
      dispatch(deleteAdminTransaction(json.data))
      return json.data
    },
  })

export const POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY =
  'POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY'
export const adminBulkDeleteTransaction = (ids: number[]) =>
  fetchWrapper({
    fetchKey: POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<{ message: string }>(
        '/finances/api/v1/admin/transactions/bulk_delete',
        { ids }
      )

      dispatch(deleteMultipleTransactions(ids))
      return json.data
    },
  })

// Split a single transaction
export const adminSplitTransaction = (
  id: string | number,
  data: SplitTransactionModel[]
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error splitting transaction',
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<Transaction>(
        `/finances/api/v1/admin/transactions/${id}/split`,
        data
      )
      const normalizedData = normalize<
        Transaction,
        TransactionResponseEntities,
        string
      >(json.data, transactionSchema)
      dispatch(receiveAdminAllTransactions(normalizedData))
      return json.data
    },
  })

export type LiteDowngradeEligibilityResponse = {
  eligible: boolean
  blockingReasons: string[]
} | null

type AdminSubscriptionsResponse = {
  subscriptions: StripeSubscription[]
  liteDowngradeEligibility: LiteDowngradeEligibilityResponse
}

export const ADMIN_REQUEST_SUBSCRIPTIONS_BY_USER_ID =
  'REQUEST_SUBSCRIPTIONS_BY_USER_ID'

export const adminFetchSubscriptionsByUserId = (userId: number) =>
  fetchWrapper({
    fetchKey: ADMIN_REQUEST_SUBSCRIPTIONS_BY_USER_ID,
    fetchFunction: async () => {
      const json = await adminApiAxios.get<AdminSubscriptionsResponse>(
        `/finances/api/v1/admin/${userId}/subscriptions`
      )

      return json.data
    },
  })
export const ADMIN_REQUEST_SUBSCRIPTIONS_BY_SUBSCRIPTION_ID =
  'REQUEST_SUBSCRIPTIONS_BY_SUBSCRIPTION_ID'
export const adminFetchSubscriptionsBySubscriptionId = (
  userId?: number,
  subscriptionId?: string
) =>
  fetchWrapper({
    fetchKey: ADMIN_REQUEST_SUBSCRIPTIONS_BY_SUBSCRIPTION_ID,
    fetchFunction: async () => {
      const json = await adminApiAxios.get<StripeSubscription>(
        `/finances/api/v1/admin/${userId}/subscriptions/${subscriptionId}`
      )

      return json.data
    },
  })
